import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';

const SeoPositions = ({ siteId, dateFrom, dateTo, currentLang, onStats }) => {
  const { token, API_URL } = useAuth();
  
  const [positions, setPositions] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Функция для получения цвета ячейки
  const getCellColor = (position) => {
    if (!position) return 'bg-gray-100 dark:bg-gray-700';
    if (position.position <= 3) return 'bg-green-200 dark:bg-green-200';
    if (position.position <= 5) return 'bg-green-100 dark:bg-green-100';
    if (position.position <= 10) return 'bg-blue-100 dark:bg-blue-900';
    if (position.position <= 20) return 'bg-yellow-100 dark:bg-yellow-900';
    return 'bg-gray-200 dark:bg-gray-600';
  };

  // Функция для получения цвета текста
  const getTextColor = (position) => {
    if (!position) return 'text-gray-500 dark:text-gray-400';
    if (position.position <= 6) return 'text-green-800 dark:text-green-200';
    if (position.position <= 10) return 'text-blue-800 dark:text-blue-200';
    if (position.position <= 20) return 'text-yellow-800 dark:text-yellow-200';
    return 'text-gray-700 dark:text-gray-300';
  };

  const getSearchEngineName = (searchId) => {
    const engines = {
      '1': 'Google',
      '2': 'Yandex',
      '3': 'Bing'
    };
    return engines[searchId] || `Поисковая система ${searchId}`;
  };

  const getSearchEngineIcon = (searchId) => {
    const engines = {
      '1': '🔍',
      '2': '🔴',
      '3': '🔵'
    };
    return engines[searchId] || '🔍';
  };

  const getSearchEngineUrl = (searchId, query) => {
    const engines = {
      '1': `https://www.google.com/search?q=${encodeURIComponent(query)}`,
      '2': `https://yandex.ru/search/?text=${encodeURIComponent(query)}`,
      '3': `https://www.bing.com/search?q=${encodeURIComponent(query)}`
    };
    return engines[searchId] || '#';
  };

  // Функция декодирования Base64
  const decodeBase64 = (str) => {
    if (!str) return '';
    const isBase64 = /^[A-Za-z0-9+/]*={0,2}$/.test(str) && str.length % 4 === 0;
    if (isBase64) {
      try {
        const decoded = atob(str);
        try {
          return decodeURIComponent(escape(decoded));
        } catch {
          return decoded;
        }
      } catch {
        return str;
      }
    } else {
      return str;
    }
  };

  // Функция парсинга ключевых слов
  const parseKeywords = (keyData) => {
    if (!keyData) return [];
    try {
      const decoded = decodeBase64(keyData);
      const keywords = [];
      const matches = decoded.match(/"([^\"]+)"/g);
      if (matches) {
        const extractedKeywords = matches.map(match => match.replace(/"/g, ''));
        keywords.push(...extractedKeywords.map(keyword => {
          try {
            return decodeURIComponent(escape(keyword));
          } catch {
            return keyword;
          }
        }));
      }
      if (keywords.length === 0) {
        const altMatches = decoded.match(/s:\d+:"([^\"]+)"/g);
        if (altMatches) {
          const altKeywords = altMatches.map(match => {
            const content = match.match(/s:\d+:"([^\"]+)"/);
            return content ? content[1] : '';
          }).filter(Boolean);
          keywords.push(...altKeywords.map(keyword => {
            try {
              return decodeURIComponent(escape(keyword));
            } catch {
              return keyword;
            }
          }));
        }
      }
      return keywords;
    } catch (error) {
      return [];
    }
  };

  // Функция для группировки данных по запросам и датам
  const processPositionsData = () => {
    if (!positions.length) return { dates: [] };

    // Получаем уникальные даты
    const uniqueDates = [...new Set(positions.map(p => p.date))].sort((a, b) => new Date(a) - new Date(b));

    return { dates: uniqueDates };
  };

  const fetchPositions = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const response = await fetch(`${API_URL}${currentLang}/seo/sites/${siteId}/positions?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const positionsData = data.data.positions || [];
        setPositions(positionsData);
      } else {
        throw new Error(data.message || 'Ошибка загрузки позиций');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPages = async () => {
    try {
      const response = await fetch(`${API_URL}${currentLang}/seo/sites/${siteId}/pages`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setPages(data.pages || []);
      } else {
        throw new Error(data.message || 'Ошибка загрузки страниц');
      }
    } catch (error) {
      
    }
  };

  useEffect(() => {
    if (siteId) {
      fetchPages();
      fetchPositions();
    }
  }, [siteId, dateFrom, dateTo]);

  // Вычисляем статистику для отчета и передаем в родителя
  useEffect(() => {
    if (!positions.length) return;
    // 1. Количество уникальных ключевых слов за период
    const uniqueKeywords = new Set();
    pages.forEach(page => {
      parseKeywords(page.key).forEach(kw => uniqueKeywords.add(kw));
    });
    // 2. Количество позиций в ТОП-10 за последний день съёма
    const { dates } = processPositionsData();
    const lastDate = dates[dates.length - 1];
    let top10Count = 0;
    let top10_google = 0;
    let top10_yandex = 0;
    let top5_google = 0;
    let top5_yandex = 0;
    if (lastDate) {
      top10Count = positions.filter(p => p.date === lastDate && p.position && Number(p.position) <= 10).length;
      top10_google = positions.filter(p => p.date === lastDate && p.position && Number(p.position) <= 10 && String(p.search_id) === '1').length;
      top10_yandex = positions.filter(p => p.date === lastDate && p.position && Number(p.position) <= 10 && String(p.search_id) === '2').length;
      top5_google = positions.filter(p => p.date === lastDate && p.position && Number(p.position) <= 5 && String(p.search_id) === '1').length;
      top5_yandex = positions.filter(p => p.date === lastDate && p.position && Number(p.position) <= 5 && String(p.search_id) === '2').length;
    }
    if (onStats) {
      onStats({
        keywordsCount: uniqueKeywords.size,
        top10Count,
        top10_google,
        top10_yandex,
        top5_google,
        top5_yandex
      });
    }
  }, [positions, pages, onStats]);

  const { dates } = processPositionsData();

  if (pages.length === 0) {
    return null;
  }

  return (
    <div className="min-h-[297mm] bg-white p-8">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          SEO Позиции
        </h2>
        <p className="text-gray-600">
          Позиции сайта в поисковых системах за период отчета
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="flex items-center space-x-2">
            <span className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></span>
            <span className="text-gray-600">Загрузка позиций...</span>
          </div>
        </div>
      ) : error ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="text-red-500 mb-2">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <p className="text-gray-600 mb-4">{error}</p>
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Google */}
          <div className="bg-white rounded-lg border p-6 border-l-4 border-l-blue-500">
            <h3 className="text-lg font-semibold mb-4 text-left flex items-center justify-start gap-2 text-blue-700">
              Google
            </h3>
            <div className="space-y-6">
              {pages.map((page) => {
                const keywords = parseKeywords(page.key);
                if (!keywords.length) return null;
                return (
                  <div key={page.id} className="border rounded-lg shadow-sm">
                    <div className="px-4 py-2 border-b bg-gray-50 rounded-t-lg">
                      <span className="font-semibold text-blue-600">{page.url}</span>
                      <span className="ml-2 text-xs text-gray-500">({keywords.length} ключевых запросов)</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse border border-gray-200">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="border border-gray-200 px-3 py-2 text-left text-sm font-medium text-gray-700 sticky left-0 bg-gray-50 z-10 w-[350px]">
                              Запрос
                            </th>
                            {dates.map((date) => (
                              <th key={date} className="border border-gray-200 px-2 py-2 text-center text-xs text-gray-700 min-w-[60px]">
                                {new Date(date).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {keywords.map((query) => (
                            <tr key={query} className="hover:bg-gray-50">
                              <td className="border border-gray-200 px-3 py-2 text-sm font-medium text-gray-900 sticky left-0 bg-white z-10">
                                <a
                                  href={getSearchEngineUrl('1', query)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="hover:underline"
                                >
                                  {query}
                                </a>
                              </td>
                              {dates.map((date) => {
                                const posData = positions.find(
                                  p => p.query === query && p.date === date && String(p.search_id) === '1'
                                );
                                return (
                                  <td
                                    key={`${query}-${date}`}
                                    className={`border border-gray-200 px-2 py-2 text-center text-sm ${getCellColor(posData)}`}
                                  >
                                    {posData ? (
                                      <a
                                        href={getSearchEngineUrl(posData.search_id, posData.query)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`font-medium ${getTextColor(posData)} hover:underline`}
                                      >
                                        {posData.position}
                                      </a>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Yandex */}
          <div className="bg-white rounded-lg border p-6 border-l-4 border-l-red-500">
            <h3 className="text-lg font-semibold mb-4 text-left flex items-center justify-start gap-2 text-red-700">
              Yandex
            </h3>
            <div className="space-y-6">
              {pages.map((page) => {
                const keywords = parseKeywords(page.key);
                if (!keywords.length) return null;
                return (
                  <div key={page.id} className="border rounded-lg shadow-sm">
                    <div className="px-4 py-2 border-b bg-gray-50 rounded-t-lg">
                      <span className="font-semibold text-blue-600">{page.url}</span>
                      <span className="ml-2 text-xs text-gray-500">({keywords.length} ключевых запросов)</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse border border-gray-200">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="border border-gray-200 px-3 py-2 text-left text-sm font-medium text-gray-700 sticky left-0 bg-gray-50 z-10 w-[350px]">
                              Запрос
                            </th>
                            {dates.map((date) => (
                              <th key={date} className="border border-gray-200 px-2 py-2 text-center text-xs text-gray-700 min-w-[60px]">
                                {new Date(date).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {keywords.map((query) => (
                            <tr key={query} className="hover:bg-gray-50">
                              <td className="border border-gray-200 px-3 py-2 text-sm font-medium text-gray-900 sticky left-0 bg-white z-10">
                                <a
                                  href={getSearchEngineUrl('2', query)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="hover:underline"
                                >
                                  {query}
                                </a>
                              </td>
                              {dates.map((date) => {
                                const posData = positions.find(
                                  p => p.query === query && p.date === date && String(p.search_id) === '2'
                                );
                                return (
                                  <td
                                    key={`${query}-${date}`}
                                    className={`border border-gray-200 px-2 py-2 text-center text-sm ${getCellColor(posData)}`}
                                  >
                                    {posData ? (
                                      <a
                                        href={getSearchEngineUrl(posData.search_id, posData.query)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`font-medium ${getTextColor(posData)} hover:underline`}
                                      >
                                        {posData.position}
                                      </a>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeoPositions; 